import { NLS_HELPER_APP_LOCALES, NLS_LOCALE_LABELS, NLS_MAJOR_LOCALES, NLS_TYPOGRAPHY, normalizeHelperAppLocale, normalizeNlsLocale } from './localePolicy'
import type { NlsInterpolationValues, NlsLocale, NlsTypographyPolicy } from './nlsTypes'
import { NLS_KEYS, STRINGS_NLS, type NlsKey, type NlsStringMap } from './strings_nls'
import { arStringsNls } from './locales/ar/strings_nls'
import { enStringsNls } from './locales/en/strings_nls'
import { esStringsNls } from './locales/es/strings_nls'
import { frStringsNls } from './locales/fr/strings_nls'
import { hiStringsNls } from './locales/hi/strings_nls'
import { ptStringsNls } from './locales/pt/strings_nls'
import { zhHansStringsNls } from './locales/zh-Hans/strings_nls'
import { bnStringsNls } from './locales/bn/strings_nls'
import { taStringsNls } from './locales/ta/strings_nls'
import { teStringsNls } from './locales/te/strings_nls'
import { mrStringsNls } from './locales/mr/strings_nls'
import { guStringsNls } from './locales/gu/strings_nls'
import { urStringsNls } from './locales/ur/strings_nls'
import { knStringsNls } from './locales/kn/strings_nls'
import { orStringsNls } from './locales/or/strings_nls'
import { mlStringsNls } from './locales/ml/strings_nls'
import { ruStringsNls } from './locales/ru/strings_nls'
import { jaStringsNls } from './locales/ja/strings_nls'
import { deStringsNls } from './locales/de/strings_nls'
import { koStringsNls } from './locales/ko/strings_nls'
import { trStringsNls } from './locales/tr/strings_nls'
import { viStringsNls } from './locales/vi/strings_nls'
import { itStringsNls } from './locales/it/strings_nls'
import { faStringsNls } from './locales/fa/strings_nls'
import { plStringsNls } from './locales/pl/strings_nls'
import { ukStringsNls } from './locales/uk/strings_nls'
import { nlStringsNls } from './locales/nl/strings_nls'
import { thStringsNls } from './locales/th/strings_nls'
import { idStringsNls } from './locales/id/strings_nls'
import { msStringsNls } from './locales/ms/strings_nls'
import { filStringsNls } from './locales/fil/strings_nls'

// Exported so the offline translation pipeline (scripts/nls/*) can inspect
// per-locale overrides directly — the runtime hot path uses LOCALE_DICTIONARIES
// (below), which pre-materializes each locale with English fallback.
export const LOCALE_STRINGS: Record<NlsLocale, Partial<NlsStringMap>> = {
  en: enStringsNls,
  'zh-Hans': zhHansStringsNls,
  es: esStringsNls,
  hi: hiStringsNls,
  fr: frStringsNls,
  ar: arStringsNls,
  pt: ptStringsNls,
  bn: bnStringsNls,
  ta: taStringsNls,
  te: teStringsNls,
  mr: mrStringsNls,
  gu: guStringsNls,
  ur: urStringsNls,
  kn: knStringsNls,
  or: orStringsNls,
  ml: mlStringsNls,
  ru: ruStringsNls,
  ja: jaStringsNls,
  de: deStringsNls,
  ko: koStringsNls,
  tr: trStringsNls,
  vi: viStringsNls,
  it: itStringsNls,
  fa: faStringsNls,
  pl: plStringsNls,
  uk: ukStringsNls,
  nl: nlStringsNls,
  th: thStringsNls,
  id: idStringsNls,
  ms: msStringsNls,
  fil: filStringsNls,
}

const buildDictionary = (locale: NlsLocale): NlsStringMap => {
  const localeStrings = LOCALE_STRINGS[locale]
  return Object.fromEntries(
    NLS_KEYS.map((key) => [key, localeStrings[key] ?? STRINGS_NLS[key].text]),
  ) as NlsStringMap
}

// Locale dictionaries are static per session; precompute once at module load
// so hot t() calls do a single object lookup instead of rebuilding a
// ~2.5k-key object on every invocation.
const LOCALE_DICTIONARIES: Record<NlsLocale, NlsStringMap> = Object.fromEntries(
  (Object.keys(LOCALE_STRINGS) as NlsLocale[]).map((locale) => [locale, buildDictionary(locale)]),
) as Record<NlsLocale, NlsStringMap>

export const interpolateNlsString = (template: string, values?: NlsInterpolationValues): string => {
  if (!values) return template
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key]
    return value === undefined ? match : String(value)
  })
}

export { NLS_HELPER_APP_LOCALES, NLS_LOCALE_LABELS, NLS_MAJOR_LOCALES, normalizeHelperAppLocale, normalizeNlsLocale }

export const getNlsTypography = (localeInput?: string | null): NlsTypographyPolicy => NLS_TYPOGRAPHY[normalizeNlsLocale(localeInput)]

export const getNlsDictionary = (localeInput?: string | null): NlsStringMap =>
  LOCALE_DICTIONARIES[normalizeNlsLocale(localeInput)]

export const getNlsString = (
  key: NlsKey,
  values?: NlsInterpolationValues,
  localeInput?: string | null,
): string => interpolateNlsString(LOCALE_DICTIONARIES[normalizeNlsLocale(localeInput)][key], values)
